import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useLanguage } from "../lib/language";
import { toast } from "sonner";
import { KeyRound, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
    const { t } = useLanguage();
    const [params] = useSearchParams();
    const nav = useNavigate();
    const token = params.get("token");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 6) { toast.error(t("Mínimo 6 caracteres", "Minimum 6 characters")); return; }
        if (password !== confirm) { toast.error(t("Las contraseñas no coinciden", "Passwords do not match")); return; }
        setLoading(true);
        try {
            await api.post("/auth/password-reset-confirm", { token, new_password: password });
            setDone(true);
            toast.success(t("Contraseña restablecida", "Password reset"));
            setTimeout(() => nav("/login"), 2500);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("Error al restablecer", "Reset error"));
        } finally { setLoading(false); }
    };

    if (!token) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center px-6">
                <div className="tb-card p-8 max-w-md text-center">
                    <h1 className="font-display text-2xl mb-2">{t("Enlace inválido", "Invalid link")}</h1>
                    <p className="text-sm text-[var(--tb-text-2)] mb-4">{t("Este enlace no contiene un token válido.", "This link does not contain a valid token.")}</p>
                    <Link to="/login" className="tb-btn-primary">{t("Ir a iniciar sesión", "Go to sign in")}</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-[70vh] flex items-center justify-center px-6 py-12 fade-in">
            <div className="tb-card p-10 w-full max-w-md">
                {!done ? (
                    <>
                        <KeyRound className="w-10 h-10 text-[var(--tb-gold)] mb-4" />
                        <h1 className="font-display text-3xl mb-2">{t("Nueva contraseña", "New password")}</h1>
                        <p className="text-sm text-[var(--tb-text-2)] mb-6">{t("Elige una contraseña segura. Mínimo 6 caracteres.", "Choose a strong password. Minimum 6 characters.")}</p>
                        <form onSubmit={submit} className="space-y-4">
                            <div>
                                <label className="tb-overline block mb-1">{t("Nueva contraseña", "New password")}</label>
                                <input type="password" required minLength={6} className="tb-input" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password" autoFocus />
                            </div>
                            <div>
                                <label className="tb-overline block mb-1">{t("Confirmar contraseña", "Confirm password")}</label>
                                <input type="password" required minLength={6} className="tb-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm" />
                            </div>
                            <button type="submit" disabled={loading} className="tb-btn-primary w-full disabled:opacity-50" data-testid="reset-submit">
                                {loading ? t("Guardando...", "Saving...") : t("Restablecer contraseña", "Reset password")}
                            </button>
                        </form>
                    </>
                ) : (
                    <div className="text-center">
                        <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                        <h1 className="font-display text-2xl mb-2">{t("¡Listo!", "Done!")}</h1>
                        <p className="text-sm text-[var(--tb-text-2)] mb-4">{t("Tu contraseña fue restablecida. Te llevaremos al login...", "Your password was reset. Taking you to sign in...")}</p>
                        <Link to="/login" className="tb-btn-primary">{t("Ir ahora", "Go now")}</Link>
                    </div>
                )}
            </div>
        </div>
    );
}
